import { useState, useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import {
  ArrowRight, CheckCircle, Calendar, Users, Clock,
  Building2, Briefcase, ClipboardList, Settings, Mail, Phone,
  Zap, Target, TrendingUp, Calculator, Minus, Plus, Heart,
  Sparkles, UserCheck, Laptop, Stethoscope, ChevronDown,
} from "lucide-react";
import SEO from "../components/SEO";
import StructuredData from "../components/StructuredData";

const useInView = (threshold = 0.12) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVisible(true); obs.disconnect(); } }, { threshold });
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
};

const Reveal = ({ children, className = "", delay = 0 }) => {
  const [ref, visible] = useInView();
  return (
    <div ref={ref} className={`transition-all ease-out ${visible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"} ${className}`} style={{ transitionDuration: "800ms", transitionDelay: `${delay}ms` }}>
      {children}
    </div>
  );
};

const WaveDivider = ({ fill = "#F7F7F7" }) => (
  <div className="relative -mb-1">
    <svg viewBox="0 0 1440 100" fill="none" xmlns="http://www.w3.org/2000/svg" className="w-full block">
      <path d="M0 100V40C180 70 360 10 540 30C720 50 900 90 1080 70C1260 50 1350 20 1440 40V100H0Z" fill={fill} />
    </svg>
  </div>
);

const AdminServices = () => {
  const [selectedTier, setSelectedTier] = useState(1);
  const [hours, setHours] = useState(30);
  const [openFaq, setOpenFaq] = useState(null);

  const tiers = [
    {
      tier: 1, title: "Essential Support", subtitle: "Reliable administrative support that keeps your day moving.", startingPrice: 35,
      features: ["Calendar and inbox management", "Appointment scheduling and confirmations", "Travel coordination", "Meeting reminders", "File and document organization", "Data entry", "Basic research", "Customer service support", "Follow-up communications"],
      bestFor: "Solo practitioners, small practices, healthcare startups, and busy professionals looking for dependable administrative support.",
      icon: ClipboardList, color: "bg-[#E8F4F5]", iconColor: "text-[#00838A]",
    },
    {
      tier: 2, title: "Operational Support", subtitle: "Better systems. Better organization. Less chaos.", startingPrice: 55,
      features: ["Workflow improvements", "Meeting preparation and follow-up", "Light project coordination", "Vendor communication", "CRM management", "Internal documentation", "SOP creation", "Task tracking", "Operational coordination"],
      bestFor: "Growing practices, multi-provider clinics, and organizations that need stronger systems without hiring a dedicated operations manager.",
      icon: Settings, color: "bg-[#00838A]/10", iconColor: "text-[#0B1F3A]", popular: true,
    },
    {
      tier: 3, title: "Strategic Operations", subtitle: "Build the operational foundation your organization needs to grow with confidence.", startingPrice: 75,
      features: ["Operational assessments", "30, 60, and 90-day action planning", "Workflow design and optimization", "SOP library development and maintenance", "Implementation support", "KPI dashboard setup", "Reporting and performance tracking", "Quality assurance processes", "Cross-functional coordination"],
      bestFor: "Growing healthcare organizations, multi-location practices, life sciences companies, and organizations looking for strategic operational support.",
      icon: TrendingUp, color: "bg-[#0B1F3A]/5", iconColor: "text-[#C9A84C]",
    },
  ];

  const painPoints = [
    "You\u2019re answering emails after hours because there wasn\u2019t enough time during the day.",
    "Your calendar, inbox, and paperwork constantly compete for your attention.",
    "You know your processes could be better, but you don\u2019t have the time to improve them.",
    "You\u2019re spending more time working in your business than growing it.",
    "You need help, but you\u2019re not ready to hire another full-time employee.",
    "You\u2019re wearing too many hats and it\u2019s starting to feel overwhelming.",
  ];

  const whyChooseUs = [
    "Dedicated support from professionals who understand healthcare",
    "Flexible service that grows with your organization",
    "Practical solutions that improve efficiency and organization",
    "Consistent communication and dependable follow-through",
    "Experienced support without the cost of another full-time employee",
    "More time to focus on patient care, leadership, and growth",
  ];

  const whoWeServe = [
    { icon: Stethoscope, title: "Solo Healthcare Providers", desc: "Spend less time managing your business and more time caring for your patients." },
    { icon: Building2, title: "Private Practices", desc: "Build the support your practice needs without hiring another full-time employee." },
    { icon: Zap, title: "Healthcare Startups", desc: "Create organized systems that support sustainable growth from day one." },
    { icon: Briefcase, title: "Life Sciences & Pharma", desc: "Keep projects organized and moving with dependable administrative and operational support." },
    { icon: Laptop, title: "Healthcare Technology", desc: "Support growing teams with flexible administrative and operational solutions." },
  ];

  const processSteps = [
    { num: "1", title: "Schedule a Consultation", desc: "We\u2019ll learn about your organization, your goals, and where you need support most." },
    { num: "2", title: "Choose Your Service Level", desc: "We\u2019ll recommend the level of support that best fits your current needs and future goals." },
    { num: "3", title: "Meet Your Dedicated Support Professional", desc: "You\u2019ll be matched with a dedicated professional who learns your workflows, preferences, and priorities, backed by the full RNB team." },
    { num: "4", title: "Get Back to What You Do Best", desc: "With the right support in place, spend less time in admin work and more time on patients, team, and growth." },
  ];

  const faqs = [
    { q: "Will I work with the same person?", a: "Yes. You\u2019ll be paired with a dedicated administrative professional who will get to know your organization, workflows, and preferences. Behind the scenes, they\u2019re supported by the broader RNB Consulting Group team, giving you the consistency of one primary point of contact with the expertise and reliability of an entire team." },
    { q: "Is there a long-term contract?", a: "No. We don\u2019t require long-term commitments. Our services are designed to be flexible, allowing you to scale your support as your organization\u2019s needs change. Whether you need ongoing support or assistance during a particularly busy season, we\u2019ll build a solution that works for you." },
    { q: "Is your team HIPAA compliant?", a: "Absolutely. We understand the importance of protecting sensitive information. Our team follows HIPAA-compliant practices and uses secure, HIPAA-compliant technology to support healthcare organizations virtually, helping you maintain confidentiality and compliance every step of the way." },
    { q: "Can you work with the systems we already use?", a: "Yes. Our team is experienced with a wide variety of electronic health records, scheduling platforms, CRMs, project management tools, and communication platforms. During onboarding, we\u2019ll learn your existing workflows and work within your preferred systems whenever possible." },
    { q: "How quickly can we get started?", a: "Once we\u2019ve had an initial consultation and determined the right level of support, we\u2019ll match you with a dedicated team member and begin onboarding. Our goal is to make the transition seamless so you can start experiencing the benefits of additional support as quickly as possible." },
    { q: "What if my needs change over time?", a: "That\u2019s exactly why our services are designed to be flexible. You can increase your monthly hours in 5-hour increments, reduce support when needed, or transition to a different service level as your organization grows." },
    { q: "Can a virtual support team really support my practice effectively?", a: "Absolutely. Many administrative and operational responsibilities can be handled remotely with the right processes and technology. From managing calendars and inboxes to coordinating projects, documenting workflows, and supporting day-to-day operations, our team integrates seamlessly into your existing processes so you receive dependable support no matter where we\u2019re located." },
  ];

  return (
    <div className="overflow-hidden" style={{ fontFamily: "'DM Sans', 'Public Sans', system-ui, sans-serif" }}>
      <SEO title="Administrative Support for Healthcare Practices" description="Flexible administrative and operational support designed for healthcare. From scheduling to workflow optimization. Starting at $35/hr." keywords="healthcare virtual assistant, medical practice admin support, healthcare administrative services, practice operations support" />
      <StructuredData data={[{ "@context": "https://schema.org", "@type": "BreadcrumbList", "itemListElement": [{ "@type": "ListItem", "position": 1, "name": "Home", "item": "https://www.rnbconsultinggroup.com" }, { "@type": "ListItem", "position": 2, "name": "Administrative Services", "item": "https://www.rnbconsultinggroup.com/administrative-services" }] }]} />

      {/* ═══ HERO ═══ */}
      <section className="relative min-h-[85vh] flex items-center bg-[#0B1F3A] overflow-hidden">
        <div className="absolute inset-0">
          <img src="https://images.pexels.com/photos/5452228/pexels-photo-5452228.jpeg?auto=compress&w=1920&h=1080&fit=crop" alt="Healthcare team" className="w-full h-full object-cover opacity-15" />
          <div className="absolute inset-0 bg-gradient-to-br from-[#0B1F3A]/95 via-[#0B1F3A]/90 to-[#00838A]/30" />
        </div>
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-20 right-[10%] w-80 h-80 bg-[#C9A84C]/8 rounded-full blur-[100px]" />
          <div className="absolute bottom-20 left-[5%] w-96 h-96 bg-[#00838A]/12 rounded-full blur-[120px]" />
        </div>

        <div className="max-w-5xl mx-auto px-5 sm:px-6 lg:px-8 py-20 relative z-10 w-full">
          <Reveal>
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/10 backdrop-blur-sm border border-white/15 mb-8">
              <Heart className="w-4 h-4 text-[#C9A84C]" />
              <span className="text-sm font-medium text-white/90">Administrative Support for Healthcare</span>
            </div>
          </Reveal>
          <Reveal delay={100}>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.08] tracking-tight text-white mb-6 max-w-3xl" style={{ fontFamily: "'Playfair Display', serif" }}>
              Spend More Time Caring for Patients. <span className="text-[#C9A84C]">We{'\u2019'}ll Help With the Rest.</span>
            </h1>
          </Reveal>
          <Reveal delay={200}>
            <p className="text-lg lg:text-xl text-white/70 leading-relaxed mb-6 max-w-2xl">
              You didn{'\u2019'}t become a healthcare provider to spend your evenings answering emails, managing schedules, chasing paperwork, or trying to keep up with an endless to-do list.
            </p>
            <p className="text-base text-white/60 leading-relaxed mb-10 max-w-2xl">
              At RNB Consulting Group, we provide flexible administrative and operational support designed specifically for healthcare organizations. Support starts at just 30 hours per month and grows with your organization.
            </p>
          </Reveal>
          <Reveal delay={300}>
            <Link to="/consultation">
              <Button size="lg" className="bg-[#00838A] hover:bg-[#00838A]/90 text-white rounded-full px-8 py-6 text-base font-semibold shadow-lg shadow-[#00838A]/25 hover:-translate-y-0.5 transition-all duration-300" data-testid="hero-cta">
                Schedule a Free Consultation <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
          </Reveal>
        </div>
        <WaveDivider fill="#F7F7F7" />
      </section>

      {/* ═══ PAIN POINTS ═══ */}
      <section className="pt-20 pb-28 bg-[#F7F7F7]">
        <div className="max-w-4xl mx-auto px-5 sm:px-6 lg:px-8">
          <Reveal>
            <div className="text-center mb-14">
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#E8F4F5] text-[#00838A] text-sm font-semibold mb-5"><Zap className="w-4 h-4" /> Sound Familiar?</span>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0B1F3A] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>Feeling Stretched Thin?</h2>
              <p className="text-lg text-[#5A5A5A] max-w-xl mx-auto">If any of these sound familiar, you{'\u2019'}re exactly who we built this service for.</p>
            </div>
          </Reveal>
          <div className="space-y-4 mb-10">
            {painPoints.map((p, i) => (
              <Reveal key={`pp-${i}`} delay={i * 70}>
                <div className="flex items-start gap-4 bg-white p-5 rounded-2xl shadow-[0_2px_12px_rgba(11,31,58,0.04)] hover:-translate-y-0.5 hover:shadow-[0_4px_20px_rgba(0,131,138,0.08)] transition-all duration-400">
                  <CheckCircle className="w-5 h-5 text-[#00838A] mt-0.5 shrink-0" />
                  <span className="text-[#0B1F3A] text-base">{p}</span>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={500}>
            <p className="text-center text-lg font-semibold text-[#0B1F3A]" style={{ fontFamily: "'Playfair Display', serif" }}>You don{'\u2019'}t have to keep doing everything yourself.</p>
          </Reveal>
        </div>
      </section>

      {/* ═══ SUPPORT INTRO ═══ */}
      <section className="py-20 bg-white">
        <div className="max-w-3xl mx-auto px-5 sm:px-6 lg:px-8 text-center">
          <Reveal>
            <h2 className="text-3xl sm:text-4xl font-bold text-[#0B1F3A] mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>Support That Meets You Where You Are</h2>
            <p className="text-[#5A5A5A] text-base leading-relaxed mb-4">Every healthcare organization has different needs, and those needs change over time. Some clients simply need reliable administrative support. Others need help creating more efficient workflows, coordinating projects, or building stronger operational systems as they grow.</p>
            <p className="text-[#5A5A5A] text-base leading-relaxed mb-4">No matter where you are today, we{'\u2019'}ll meet you there.</p>
            <p className="text-[#00838A] font-semibold">Choose the level of support that{'\u2019'}s right for your organization, with the flexibility to grow as your needs evolve.</p>
          </Reveal>
        </div>
      </section>

      {/* ═══ SERVICE TIERS ═══ */}
      <section className="py-24 bg-[#F7F7F7]">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-3 gap-7">
            {tiers.map((tier, idx) => (
              <Reveal key={tier.tier} delay={idx * 120}>
                <Card className={`relative border-0 rounded-3xl overflow-hidden transition-all duration-500 hover:-translate-y-2 ${tier.popular ? "shadow-[0_8px_40px_rgba(0,131,138,0.15)] ring-2 ring-[#00838A]/25" : "bg-white shadow-[0_4px_24px_rgba(11,31,58,0.06)] hover:shadow-[0_12px_40px_rgba(0,131,138,0.1)]"}`} data-testid={`tier-card-${tier.tier}`}>
                  {tier.popular && <div className="bg-[#C9A84C] text-[#0B1F3A] text-center text-xs font-bold py-1.5">Most Popular</div>}
                  <CardContent className="p-8 bg-white">
                    <div className={`w-14 h-14 ${tier.color} rounded-xl flex items-center justify-center mb-5`}>
                      <tier.icon className={`w-7 h-7 ${tier.iconColor}`} />
                    </div>
                    <h3 className="text-2xl font-bold text-[#0B1F3A] mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>{tier.title}</h3>
                    <p className="text-sm text-[#5A5A5A] mb-5">{tier.subtitle}</p>
                    <div className="mb-5 pb-5 border-b border-gray-100">
                      <span className="text-sm text-[#5A5A5A]">Starting at</span>
                      <div className="text-4xl font-bold text-[#0B1F3A]" style={{ fontFamily: "'Playfair Display', serif" }}>${tier.startingPrice}<span className="text-base font-normal text-[#5A5A5A]">/hr</span></div>
                    </div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-[#00838A] mb-4">What{'\u2019'}s Included</p>
                    <div className="space-y-2.5 mb-6">
                      {tier.features.map((f, fi) => (
                        <div key={fi} className="flex items-start gap-2.5">
                          <CheckCircle className="w-4 h-4 text-[#00838A] shrink-0 mt-0.5" />
                          <span className="text-sm text-[#5A5A5A]">{f}</span>
                        </div>
                      ))}
                    </div>
                    <div className="bg-[#E8F4F5]/50 rounded-xl p-4 mb-6">
                      <p className="text-xs font-semibold text-[#0B1F3A] mb-1">Best for:</p>
                      <p className="text-xs text-[#5A5A5A] leading-relaxed">{tier.bestFor}</p>
                    </div>
                    <Link to="/consultation">
                      <Button className={`w-full rounded-full ${tier.popular ? "bg-[#00838A] hover:bg-[#00838A]/90 shadow-md" : "bg-[#0B1F3A] hover:bg-[#0B1F3A]/90"}`}>
                        Talk to Sales <ArrowRight className="ml-2 w-4 h-4" />
                      </Button>
                    </Link>
                  </CardContent>
                </Card>
              </Reveal>
            ))}
          </div>
          <Reveal delay={200}>
            <div className="mt-10 text-center">
              <div className="inline-flex items-center gap-3 bg-white rounded-full px-6 py-3 shadow-sm">
                <Clock className="w-5 h-5 text-[#00838A]" />
                <span className="text-[#0B1F3A] text-sm"><strong>Minimum:</strong> 30 hours/month &nbsp;|&nbsp; <strong>Scale:</strong> 5-hour increments as you grow</span>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ═══ PRICING CALCULATOR ═══ */}
      <section className="py-24 bg-white">
        <div className="max-w-4xl mx-auto px-5 sm:px-6 lg:px-8">
          <Reveal>
            <div className="text-center mb-12">
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#E8F4F5] text-[#00838A] text-sm font-semibold mb-5"><Calculator className="w-4 h-4" /> Estimate Your Investment</span>
              <h2 className="text-3xl sm:text-4xl font-bold text-[#0B1F3A] mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>Estimate Your Monthly Investment</h2>
              <p className="text-[#5A5A5A]">Use the calculator below to estimate your monthly investment.</p>
            </div>
          </Reveal>
          <Reveal delay={100}>
            <Card className="border-0 shadow-xl rounded-3xl overflow-hidden" data-testid="pricing-calculator">
              <CardContent className="p-0">
                <div className="grid grid-cols-3 border-b">
                  {tiers.map((t) => (
                    <button key={t.tier} onClick={() => setSelectedTier(t.tier)} data-testid={`calc-tier-${t.tier}`}
                      className={`py-5 px-4 text-center transition-all ${selectedTier === t.tier ? "bg-[#0B1F3A] text-white" : "bg-white text-[#5A5A5A] hover:bg-[#F7F7F7]"}`}>
                      <p className="text-xs font-semibold uppercase tracking-wider opacity-70 mb-1">{t.title}</p>
                      <p className="text-xl font-bold" style={{ fontFamily: "'Playfair Display', serif" }}>${t.startingPrice}/hr</p>
                    </button>
                  ))}
                </div>
                <div className="p-8 sm:p-10">
                  <div className="mb-10">
                    <label className="block text-sm font-medium text-[#5A5A5A] mb-4">Hours per month</label>
                    <div className="flex items-center gap-4 justify-center">
                      <button onClick={() => setHours((h) => Math.max(30, h - 5))} disabled={hours <= 30} data-testid="calc-hours-minus"
                        className="w-11 h-11 rounded-full border-2 border-gray-200 flex items-center justify-center text-[#0B1F3A] hover:border-[#00838A] hover:text-[#00838A] transition-colors disabled:opacity-30 disabled:cursor-not-allowed">
                        <Minus className="w-5 h-5" />
                      </button>
                      <div className="text-center min-w-[100px]">
                        <span className="text-5xl font-bold text-[#0B1F3A]" data-testid="calc-hours-value" style={{ fontFamily: "'Playfair Display', serif" }}>{hours}</span>
                        <p className="text-xs text-[#5A5A5A] mt-1">hrs / month</p>
                      </div>
                      <button onClick={() => setHours((h) => Math.min(200, h + 5))} disabled={hours >= 200} data-testid="calc-hours-plus"
                        className="w-11 h-11 rounded-full border-2 border-gray-200 flex items-center justify-center text-[#0B1F3A] hover:border-[#00838A] hover:text-[#00838A] transition-colors disabled:opacity-30 disabled:cursor-not-allowed">
                        <Plus className="w-5 h-5" />
                      </button>
                    </div>
                    <div className="mt-6 px-2">
                      <input type="range" min={30} max={200} step={5} value={hours} onChange={(e) => setHours(Number(e.target.value))} data-testid="calc-hours-slider"
                        className="w-full h-2 bg-gray-200 rounded-full appearance-none cursor-pointer accent-[#00838A] [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-5 [&::-webkit-slider-thumb]:h-5 [&::-webkit-slider-thumb]:bg-[#00838A] [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:shadow-md" />
                      <div className="flex justify-between text-xs text-[#5A5A5A] mt-2"><span>30 hrs</span><span>100 hrs</span><span>200 hrs</span></div>
                    </div>
                  </div>
                  <div className="bg-[#0B1F3A] rounded-2xl p-8 text-center" data-testid="calc-result">
                    <p className="text-white/50 text-sm mb-2">Estimated Monthly Investment</p>
                    <p className="text-5xl sm:text-6xl font-bold text-white mb-1" style={{ fontFamily: "'Playfair Display', serif" }}>
                      ${(tiers.find((t) => t.tier === selectedTier)?.startingPrice * hours).toLocaleString()}
                    </p>
                    <p className="text-white/40 text-sm">{hours} hrs &times; ${tiers.find((t) => t.tier === selectedTier)?.startingPrice}/hr &middot; {tiers.find((t) => t.tier === selectedTier)?.title}</p>
                    <div className="mt-6">
                      <Link to="/consultation"><Button size="lg" className="bg-[#00838A] hover:bg-[#00838A]/90 rounded-full shadow-lg"><Calendar className="mr-2 h-4 w-4" /> Schedule a Free Consultation</Button></Link>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </Reveal>
        </div>
      </section>

      {/* ═══ WHY CHOOSE US ═══ */}
      <section className="py-24 bg-[#0B1F3A] relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 right-[15%] w-80 h-80 bg-[#00838A]/12 rounded-full blur-[100px]" />
          <div className="absolute bottom-0 left-[10%] w-72 h-72 bg-[#C9A84C]/8 rounded-full blur-[100px]" />
        </div>
        <div className="max-w-4xl mx-auto px-5 sm:px-6 lg:px-8 relative z-10">
          <Reveal>
            <div className="text-center mb-12">
              <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>Why Healthcare Organizations Choose RNB</h2>
              <p className="text-white/60 text-base max-w-xl mx-auto">Healthcare is different. The demands are unique, the pace is fast, and every detail matters. You need a support team that understands.</p>
            </div>
          </Reveal>
          <div className="grid md:grid-cols-2 gap-4">
            {whyChooseUs.map((item, i) => (
              <Reveal key={`wcu-${i}`} delay={i * 70}>
                <div className="flex items-start gap-3 p-5 bg-white/[0.07] rounded-xl border border-white/10 backdrop-blur-sm hover:bg-white/[0.12] transition-all duration-300">
                  <CheckCircle className="w-5 h-5 text-[#C9A84C] shrink-0 mt-0.5" />
                  <span className="text-white/90 text-sm">{item}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <WaveDivider fill="#E8F4F5" />

      {/* ═══ WHO WE SERVE ═══ */}
      <section className="pt-16 pb-24 bg-[#E8F4F5]">
        <div className="max-w-7xl mx-auto px-5 sm:px-6 lg:px-8">
          <Reveal>
            <div className="text-center mb-14">
              <h2 className="text-3xl sm:text-4xl font-bold text-[#0B1F3A] mb-3" style={{ fontFamily: "'Playfair Display', serif" }}>Who We Serve</h2>
              <p className="text-[#5A5A5A]">Our Administrative Support services are designed specifically for healthcare and healthcare-adjacent organizations.</p>
            </div>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {whoWeServe.map((item, i) => (
              <Reveal key={`ws-${i}`} delay={i * 80}>
                <Card className="border-0 bg-white shadow-[0_4px_16px_rgba(11,31,58,0.04)] hover:shadow-[0_8px_30px_rgba(0,131,138,0.1)] rounded-2xl hover:-translate-y-1 transition-all duration-400">
                  <CardContent className="p-6">
                    <div className="w-12 h-12 rounded-xl bg-[#E8F4F5] flex items-center justify-center mb-4"><item.icon className="w-6 h-6 text-[#00838A]" /></div>
                    <h3 className="text-lg font-bold text-[#0B1F3A] mb-2" style={{ fontFamily: "'Playfair Display', serif" }}>{item.title}</h3>
                    <p className="text-sm text-[#5A5A5A] leading-relaxed">{item.desc}</p>
                  </CardContent>
                </Card>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ HOW IT WORKS ═══ */}
      <section className="py-24 bg-white">
        <div className="max-w-5xl mx-auto px-5 sm:px-6 lg:px-8">
          <Reveal>
            <div className="text-center mb-16">
              <span className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#E8F4F5] text-[#00838A] text-sm font-semibold mb-5"><Sparkles className="w-4 h-4" /> How It Works</span>
              <h2 className="text-3xl sm:text-4xl font-bold text-[#0B1F3A]" style={{ fontFamily: "'Playfair Display', serif" }}>Getting Started Is Simple</h2>
            </div>
          </Reveal>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {processSteps.map((s, i) => (
              <Reveal key={`ps-${i}`} delay={i * 100}>
                <div className="text-center">
                  <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-[#0B1F3A] to-[#00838A] text-white font-bold text-2xl flex items-center justify-center mx-auto mb-5 shadow-lg" style={{ fontFamily: "'Playfair Display', serif" }}>{s.num}</div>
                  <h3 className="font-bold text-[#0B1F3A] text-base mb-2">{s.title}</h3>
                  <p className="text-sm text-[#5A5A5A] leading-relaxed">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ═══ FAQ ═══ */}
      <section className="py-24 bg-[#F7F7F7]">
        <div className="max-w-3xl mx-auto px-5 sm:px-6 lg:px-8">
          <Reveal>
            <div className="text-center mb-14">
              <h2 className="text-3xl sm:text-4xl font-bold text-[#0B1F3A]" style={{ fontFamily: "'Playfair Display', serif" }}>Frequently Asked Questions</h2>
            </div>
          </Reveal>
          <div className="space-y-3">
            {faqs.map((f, i) => {
              const isOpen = openFaq === i;
              return (
                <Reveal key={`faq-${i}`} delay={i * 50}>
                  <div className={`border rounded-2xl transition-all ${isOpen ? "border-[#00838A]/20 shadow-md bg-white" : "border-transparent bg-white hover:shadow-sm"}`}>
                    <button onClick={() => setOpenFaq(isOpen ? null : i)} className="w-full flex items-center justify-between p-5 sm:p-6 text-left">
                      <span className="font-bold text-[#0B1F3A] text-sm sm:text-base pr-4">{f.q}</span>
                      <ChevronDown className={`w-5 h-5 text-[#00838A] shrink-0 transition-transform duration-300 ${isOpen ? "rotate-180" : ""}`} />
                    </button>
                    <div className={`overflow-hidden transition-all duration-300 ${isOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"}`}>
                      <p className="px-5 sm:px-6 pb-5 sm:pb-6 text-[#5A5A5A] text-sm leading-relaxed">{f.a}</p>
                    </div>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* ═══ FINAL CTA ═══ */}
      <section className="py-24 lg:py-28 bg-[#0B1F3A] relative overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 left-[20%] w-80 h-80 bg-[#C9A84C]/8 rounded-full blur-[100px]" />
        </div>
        <Reveal>
          <div className="max-w-4xl mx-auto px-5 sm:px-6 lg:px-8 text-center relative z-10">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6" style={{ fontFamily: "'Playfair Display', serif" }}>
              Let{'\u2019'}s Take Something <span className="text-[#C9A84C]">Off Your Plate</span>
            </h2>
            <p className="text-lg text-white/65 mb-4 max-w-2xl mx-auto">Running a healthcare organization comes with enough responsibilities. You shouldn{'\u2019'}t have to carry every administrative task on your own.</p>
            <p className="text-base text-white/50 mb-10 max-w-2xl mx-auto">Let{'\u2019'}s create a support plan that gives you back your time, supports your team, and helps your organization run more efficiently.</p>
            <Link to="/consultation">
              <Button size="lg" className="bg-[#C9A84C] text-[#0B1F3A] hover:bg-[#C9A84C]/90 rounded-full px-8 py-6 text-base font-bold shadow-lg hover:-translate-y-0.5 transition-all duration-300" data-testid="final-cta">
                Schedule Your Free Consultation <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            </Link>
            <div className="mt-10 pt-8 border-t border-white/10 flex flex-col sm:flex-row items-center justify-center gap-6">
              <a href="mailto:info@rnbconsultinggroup.com" className="flex items-center gap-2 text-white/50 hover:text-white/80 transition-colors text-sm"><Mail className="w-4 h-4" /> info@rnbconsultinggroup.com</a>
              <a href="tel:888-250-4605" className="flex items-center gap-2 text-white/50 hover:text-white/80 transition-colors text-sm"><Phone className="w-4 h-4" /> (888) 250-4605</a>
            </div>
          </div>
        </Reveal>
      </section>
    </div>
  );
};

export default AdminServices;
